import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { OrdersService } from './notifications-list.service';
import { ExcelService } from 'src/app/shared/excel';

interface Notification {
  isWatched: boolean;
  type: string;
  threshold: string | number;
  transactionObj: any;
  createdAt: string;
  createdAtDate: Date;
  createdAtUnix: number;
  message: string;
  productName: string;
  productId: string;
  system: string;
  systemId: string;
  machineCode: string;
  stockBefore: number | null;
  stockLeft: number | null;
  stockStatus: 'real' | 'stock-left' | 'unknown';
}

/** One line in the grouped view: a product inside a system, or a system inside a product. */
interface StockoutRow {
  label: string;
  count: number;
  lastSeen: string;
  lastSeenUnix: number;
}

interface StockoutGroup {
  key: string;
  count: number;
  rows: StockoutRow[];
}

@Component({
  selector: 'app-orders',
  templateUrl: './notifications-list.component.html',
  styleUrls: ['./notifications-list.component.css']
})
export class NotificationListComponent implements OnInit, OnDestroy {

  notifications: Notification[] = [];
  filtered: Notification[] = [];
  groups: StockoutGroup[] = [];

  // Filter state
  period = 'LAST-7-DAYS';
  customStart: Date = null;
  customEnd: Date = null;
  search = '';
  systemFilter = 'all';
  productFilter = 'all';
  // 'real' hides alerts where the system still had the product after the purchase
  stockFilter: 'real' | 'stock-left' | 'all' = 'real';
  groupBy: 'system' | 'product' = 'system';
  view: 'cards' | 'list' = 'cards';

  // Options built from whatever was loaded for the selected period
  systemOptions: string[] = [];
  productOptions: string[] = [];

  loading = true;
  rangeLabel = '';

  // Computed in buildGroups() - never bind getters that allocate into the template
  totalEvents = 0;
  totalSystems = 0;
  totalProducts = 0;

  private subscription: Subscription;

  constructor(private orderService: OrdersService, private excelService: ExcelService) { }

  ngOnInit() {
    this.load();
  }

  /** (Re)subscribe with the current period. Only this changes the Firestore query. */
  load() {
    const range = this.resolveRange();
    if (!range) { return; }

    this.rangeLabel = moment(range.start).format('DD.MM.YYYY') + ' - ' + moment(range.end).format('DD.MM.YYYY');
    this.loading = true;

    if (this.subscription) { this.subscription.unsubscribe(); }
    this.subscription = this.orderService.getNotifications$(range.start, range.end)
      .subscribe((data: any) => {
        this.notifications = (data as Notification[])
          .sort((a, b) => b.createdAtUnix - a.createdAtUnix);
        this.buildOptions();
        this.applyFilters();
        this.loading = false;
      }, () => {
        this.loading = false;
      });
  }

  private resolveRange(): { start: Date, end: Date } {
    switch (this.period) {
      case 'TODAY':
        return { start: moment().startOf('day').toDate(), end: moment().endOf('day').toDate() };
      case 'YESTERDAY':
        return {
          start: moment().subtract(1, 'day').startOf('day').toDate(),
          end: moment().subtract(1, 'day').endOf('day').toDate()
        };
      case 'LAST-7-DAYS':
        return { start: moment().subtract(6, 'days').startOf('day').toDate(), end: moment().endOf('day').toDate() };
      case 'LAST-14-DAYS':
        return { start: moment().subtract(13, 'days').startOf('day').toDate(), end: moment().endOf('day').toDate() };
      case 'LAST-30-DAYS':
        return { start: moment().subtract(29, 'days').startOf('day').toDate(), end: moment().endOf('day').toDate() };
      case 'THIS_MONTH':
        return { start: moment().startOf('month').toDate(), end: moment().endOf('day').toDate() };
      case 'LAST_MONTH':
        return {
          start: moment().subtract(1, 'month').startOf('month').toDate(),
          end: moment().subtract(1, 'month').endOf('month').toDate()
        };
      case 'CUSTOM':
        if (!this.customStart || !this.customEnd) { return null; }
        return {
          start: moment(this.customStart).startOf('day').toDate(),
          end: moment(this.customEnd).endOf('day').toDate()
        };
      default:
        return { start: moment().subtract(6, 'days').startOf('day').toDate(), end: moment().endOf('day').toDate() };
    }
  }

  setPeriod() {
    if (this.period !== 'CUSTOM') {
      this.customStart = null;
      this.customEnd = null;
    }
    this.load();
  }

  setCustomRange(event: any, which: 'start' | 'end') {
    if (which === 'start') {
      this.customStart = event.value;
    } else {
      this.customEnd = event.value;
    }
    if (this.customStart && this.customEnd) {
      this.load();
    }
  }

  private buildOptions() {
    const systems = new Set<string>();
    const products = new Set<string>();
    this.notifications.forEach(n => {
      systems.add(n.system);
      products.add(n.productName);
    });
    this.systemOptions = Array.from(systems).sort((a, b) => a.localeCompare(b));
    this.productOptions = Array.from(products).sort((a, b) => a.localeCompare(b));

    // Drop a selection that no longer exists in the new period
    if (this.systemFilter !== 'all' && !systems.has(this.systemFilter)) { this.systemFilter = 'all'; }
    if (this.productFilter !== 'all' && !products.has(this.productFilter)) { this.productFilter = 'all'; }
  }

  /** Runs on every filter/search change - never call this from the template. */
  applyFilters() {
    const term = (this.search || '').trim().toLowerCase();

    this.filtered = this.notifications.filter(n => {
      if (this.systemFilter !== 'all' && n.system !== this.systemFilter) { return false; }
      if (this.productFilter !== 'all' && n.productName !== this.productFilter) { return false; }
      // Alerts without a stock snapshot can't be judged, so they count as real.
      if (this.stockFilter === 'real' && n.stockStatus === 'stock-left') { return false; }
      if (this.stockFilter === 'stock-left' && n.stockStatus !== 'stock-left') { return false; }
      if (!term) { return true; }
      return (n.productName || '').toLowerCase().includes(term)
        || (n.system || '').toLowerCase().includes(term)
        || (n.machineCode || '').toLowerCase().includes(term)
        || (n.message || '').toLowerCase().includes(term);
    });

    this.buildGroups();
  }

  /**
   * Collapses the filtered rows into "system -> products" or "product -> systems",
   * with an occurrence count and the newest timestamp per line.
   */
  private buildGroups() {
    const map: { [key: string]: { [label: string]: StockoutRow } } = {};
    const totals: { [key: string]: number } = {};

    this.filtered.forEach(n => {
      const key = this.groupBy === 'system' ? n.system : n.productName;
      const label = this.groupBy === 'system' ? n.productName : n.system;

      if (!map[key]) { map[key] = {}; totals[key] = 0; }
      totals[key]++;

      const row = map[key][label];
      if (!row) {
        map[key][label] = {
          label,
          count: 1,
          lastSeen: n.createdAt,
          lastSeenUnix: n.createdAtUnix
        };
      } else {
        row.count++;
        if (n.createdAtUnix > row.lastSeenUnix) {
          row.lastSeen = n.createdAt;
          row.lastSeenUnix = n.createdAtUnix;
        }
      }
    });

    this.groups = Object.keys(map)
      .map(key => ({
        key,
        count: totals[key],
        rows: Object.keys(map[key])
          .map(label => map[key][label])
          .sort((a, b) => b.count - a.count || b.lastSeenUnix - a.lastSeenUnix)
      }))
      .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));

    this.totalEvents = this.filtered.length;
    this.totalSystems = new Set(this.filtered.map(f => f.system)).size;
    this.totalProducts = new Set(this.filtered.map(f => f.productName)).size;
  }

  clearFilters() {
    this.search = '';
    this.systemFilter = 'all';
    this.productFilter = 'all';
    this.stockFilter = 'real';
    this.applyFilters();
  }

  exportAsXLSX() {
    this.excelService.exportAsExcelFile(
      this.filtered,
      'Stockout',
      null,
      ['system', 'productName', 'machineCode', 'type', 'message', 'threshold', 'stockBefore', 'stockLeft', 'stockStatus', 'createdAt']
    );
  }

  /** Exports the aggregated view (one line per product/system pair) instead of raw events. */
  exportSummaryAsXLSX() {
    const rows = [];
    this.groups.forEach(group => {
      group.rows.forEach(row => {
        rows.push({
          [this.groupBy === 'system' ? 'system' : 'productName']: group.key,
          [this.groupBy === 'system' ? 'productName' : 'system']: row.label,
          stockouts: row.count,
          lastStockout: row.lastSeen
        });
      });
    });
    this.excelService.exportAsExcelFile(rows, 'Stockout_summary');
  }

  ngOnDestroy() {
    if (this.subscription) { this.subscription.unsubscribe(); }
  }
}
